import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { z } from 'zod';

const INSTALLATION_KEY = 'tupai-push-installation';
const REGISTRATIONS_KEY = 'tupai-push-registrations';
const BASE = import.meta.env.VITE_PARENT_API_URL || 'http://localhost:3006';
let pending: Promise<unknown> = Promise.resolve();
type TokenType = 'fcm' | 'apns';
const removalCredentialsSchema = z
  .array(z.object({ token_type: z.enum(['fcm', 'apns']), removal_token: z.string().min(1) }))
  .min(1)
  .max(2)
  .refine(
    (credentials) => new Set(credentials.map((row) => row.token_type)).size === credentials.length
  );
type RegistrationOptions =
  | { platform: 'android' | 'ios'; tokenType: 'fcm'; apnsEnvironment?: never }
  | { platform: 'ios'; tokenType: 'apns'; apnsEnvironment: 'sandbox' | 'production' };
type SavedRegistration = {
  installationId: string;
  tokenType: TokenType;
  removalToken: string;
  pending: boolean;
};

async function getPushInstallationId(): Promise<string> {
  const { value } = await Preferences.get({ key: INSTALLATION_KEY });
  if (value) return value;
  const id = crypto.randomUUID();
  await Preferences.set({ key: INSTALLATION_KEY, value: id });
  return id;
}

function serialize<T>(operation: () => Promise<T>): Promise<T> {
  const result = pending.then(operation);
  pending = result.catch(() => {});
  return result;
}

async function readRegistrations(): Promise<SavedRegistration[]> {
  const { value } = await Preferences.get({ key: REGISTRATIONS_KEY });
  const registrations: SavedRegistration[] = value ? JSON.parse(value) : [];
  return registrations.map((registration) => ({
    ...registration,
    tokenType: registration.tokenType ?? 'fcm',
  }));
}

async function saveRegistrations(registrations: SavedRegistration[]): Promise<void> {
  await Preferences.set({ key: REGISTRATIONS_KEY, value: JSON.stringify(registrations) });
}

async function send(path: string, method: string, body: object, session?: string) {
  return fetch(`${BASE}${path}`, {
    method,
    headers: {
      ...(session && { Authorization: `Bearer ${session}` }),
      'X-App-Version': import.meta.env.VITE_APP_VERSION || '0.0.0',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(5000),
  });
}

export function uploadPushRegistration(
  token: string,
  session: string,
  isCurrentSession: () => boolean,
  options: RegistrationOptions = { platform: 'android', tokenType: 'fcm' }
): Promise<void> {
  return serialize(async () => {
    if (!isCurrentSession()) return;
    let id = await getPushInstallationId();
    if (!isCurrentSession()) return;
    const upload = () =>
      send(
        '/push-devices',
        'PUT',
        {
          installation_id: id,
          platform: options.platform,
          token_type: options.tokenType,
          token,
          ...(options.tokenType === 'apns' && { apns_environment: options.apnsEnvironment }),
        },
        session
      );
    let response = await upload();
    if (response.status === 409 && isCurrentSession()) {
      const registrations = await readRegistrations();
      await saveRegistrations(
        registrations.map((registration) =>
          registration.installationId === id ? { ...registration, pending: true } : registration
        )
      );
      id = crypto.randomUUID();
      await Preferences.set({ key: INSTALLATION_KEY, value: id });
      if (!isCurrentSession()) return;
      response = await upload();
    }
    if (!response.ok) throw new Error(`Push registration HTTP ${response.status}`);
    const body = await response.json();
    const parsed = removalCredentialsSchema.safeParse(
      body.removal_tokens ??
        (options.platform === 'android'
          ? [{ token_type: options.tokenType, removal_token: body.removal_token }]
          : [])
    );
    if (
      !parsed.success ||
      !parsed.data.some((credential) => credential.token_type === options.tokenType)
    ) {
      throw new Error('Push registration response is missing valid removal credentials');
    }
    const credentials = parsed.data;
    const registrations = (await readRegistrations()).filter(
      (registration) =>
        registration.installationId !== id ||
        registration.pending ||
        !credentials.some((credential) => credential.token_type === registration.tokenType)
    );
    registrations.push(
      ...credentials.map((credential) => ({
        installationId: id,
        tokenType: credential.token_type,
        removalToken: credential.removal_token,
        pending: !isCurrentSession(),
      }))
    );
    await saveRegistrations(registrations);
  });
}

async function removePendingRegistrations(signedOut: boolean): Promise<void> {
  let registrations = await readRegistrations();
  if (signedOut) {
    registrations = registrations.map((registration) => ({ ...registration, pending: true }));
    await saveRegistrations(registrations);
  }
  for (const registration of [...registrations]) {
    if (!registration.pending) continue;
    try {
      const response = await send('/push-devices/remove', 'POST', {
        removal_token: registration.removalToken,
      });
      if (!response.ok) continue;
      registrations = registrations.filter((entry) => entry !== registration);
      await saveRegistrations(registrations);
    } catch {
      console.warn('[push] Server removal pending; retry on reconnect.');
    }
  }
}

export function retryPushRemovals(signedOut = false): Promise<void> {
  return serialize(() => removePendingRegistrations(signedOut));
}

export function revokePushRegistration(): void {
  if (!['android', 'ios'].includes(Capacitor.getPlatform())) return;
  void retryPushRemovals(true).catch(() => {
    console.warn('[push] Unable to persist pending registration removal.');
  });
}
